import { useEffect } from 'react'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import { DEFAULT_LOCALE, isLocale, type Locale } from './types'

/**
 * The language of the page currently being viewed, taken from the URL.
 *
 * The URL is the single source of truth — i18next is told to follow it, rather
 * than the other way round. That keeps a shared link, a bookmark and the
 * back button all showing the same language they did when they were made.
 */
export function useLocale(): Locale {
  const params = useParams()
  const { i18n } = useTranslation()

  const locale = isLocale(params.locale) ? params.locale : DEFAULT_LOCALE

  useEffect(() => {
    if (i18n.language !== locale) {
      void i18n.changeLanguage(locale)
    }
  }, [i18n, locale])

  // Server rendering has no effects, so the language is set synchronously
  // there or the prerendered HTML would come out in the wrong language.
  if (typeof document === 'undefined' && i18n.language !== locale) {
    void i18n.changeLanguage(locale)
  }

  return locale
}
